import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ReactQuill from "react-quill-new";
import "quill/dist/quill.snow.css";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL || "https://upsc-backend-353e.onrender.com";

const quillModules = {
  clipboard: {
    matchVisual: false,
  },
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote"],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "blockquote",
  "link",
];

const optionLabels = ["(a)", "(b)", "(c)", "(d)"];

const EditPrelimsTest = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [timerMinutes, setTimerMinutes] = useState("");
  const [section, setSection] = useState("");
  const [questions, setQuestions] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showPreview, setShowPreview] = useState(true);
  const [isFree, setIsFree] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [currentQuestion, setCurrentQuestion] = useState({
    question: "",
    options: ["", "", "", ""],
    correctAnswer: "",
    explanation: "",
    images: [],
    tags: "",
  });

  useEffect(() => {
    fetchTest();
  }, []);

  const fetchTest = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.token;

      const res = await axios.get(`${API_BASE}/api/admin/test-series/prelims`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const test = res.data.find((t) => t._id === id);

      if (!test) {
        alert("Test not found");
        return;
      }

      setTitle(test.title || "");
      setDate(test.date ? new Date(test.date).toISOString().split("T")[0] : "");
      setTimerMinutes(test.timerMinutes || "");
      setSection(test.section || "");
      setIsFree(test.isFree ?? true);
      setQuestions(test.questions || []);
    } catch (err) {
      console.error(err);
      alert("Failed to load test");
    }
  };

  const inputStyle =
    "w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-950";

  const labelStyle =
    "mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-200";

  const secondaryButton =
    "inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800";

  const validateQuestion = (q) => {
    const plainQuestion = q.question.replace(/<[^>]*>/g, "").trim();

    if (!plainQuestion) {
      alert("Question is required");
      return false;
    }

    if (
      !Array.isArray(q.options) ||
      q.options.length !== 4 ||
      q.options.some((o) => !o.trim())
    ) {
      alert("All four options are required");
      return false;
    }

    if (!q.correctAnswer.trim()) {
      alert("Select a correct answer");
      return false;
    }

    if (!q.options.includes(q.correctAnswer)) {
      alert("Correct answer must match one of the options");
      return false;
    }

    return true;
  };

  const uploadQuestionImage = async (file) => {
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.token;
      const formData = new FormData();

      formData.append("image", file);
      formData.append("uploadType", "question");

      const res = await axios.post(
        `${API_BASE}/api/admin/upload-question-image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return res.data.url;
    } catch (err) {
      console.error(err);
      alert("Image upload failed");
      return null;
    }
  };

  const handleAddQuestion = () => {
    const q = {
      question: currentQuestion.question,
      options: currentQuestion.options.map((o) => o.trim()),
      correctAnswer: currentQuestion.correctAnswer.trim(),
      explanation: currentQuestion.explanation.trim(),
      images: currentQuestion.images || [],
      tags: currentQuestion.tags
        ? currentQuestion.tags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
        : [],
    };

    if (!validateQuestion(q)) return;

    if (editingIndex !== null) {
      setQuestions((prev) =>
        prev.map((item, idx) => (idx === editingIndex ? q : item))
      );
      setEditingIndex(null);
    } else {
      setQuestions((prev) => [...prev, q]);
    }

    setCurrentQuestion({
      question: "",
      options: ["", "", "", ""],
      correctAnswer: "",
      explanation: "",
      images: [],
      tags: "",
    });
  };

  const handleSubmit = async (status = "draft") => {
    if (!title || !date || !timerMinutes || questions.length === 0) {
      alert("Please fill all required fields and add at least one question.");
      return;
    }

    const timerNum = Number(timerMinutes);

    if (isNaN(timerNum) || timerNum <= 0) {
      alert("Timer must be a positive number");
      return;
    }

    try {
      const token = JSON.parse(localStorage.getItem("user"))?.token;

      await axios.put(
        `${API_BASE}/api/admin/test-series/prelims/${id}`,
        {
          title,
          date,
          timerMinutes: timerNum,
          section,
          type: "prelims",
          status,
          isFree,
          questions,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      alert(
        status === "draft"
          ? "Test saved as draft. You can publish it later."
          : "Test updated successfully."
      );
      navigate("/admin/test-series/prelims/view");
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.message || "Error uploading test.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 text-slate-900 dark:bg-slate-950 dark:text-slate-100 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400">
              Prelims Test Series
            </p>
            <h2 className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
              Edit Prelims Test
            </h2>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center sm:min-w-80">
            <div className="rounded-lg bg-slate-100 px-3 py-2 dark:bg-slate-800">
              <p className="text-xs text-slate-500 dark:text-slate-400">Questions</p>
              <p className="text-lg font-bold">{questions.length}</p>
            </div>
            <div className="rounded-lg bg-slate-100 px-3 py-2 dark:bg-slate-800">
              <p className="text-xs text-slate-500 dark:text-slate-400">Access</p>
              <p className="text-lg font-bold">{isFree ? "Free" : "Premium"}</p>
            </div>
            <div className="rounded-lg bg-slate-100 px-3 py-2 dark:bg-slate-800">
              <p className="text-xs text-slate-500 dark:text-slate-400">Mode</p>
              <p className="text-lg font-bold">
                {editingIndex !== null ? "Edit" : "Review"}
              </p>
            </div>
          </div>
        </div>

        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="mb-4">
            <h3 className="text-lg font-bold text-slate-950 dark:text-white">
              Test Details
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Update the core information for this test.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className={labelStyle}>Test Title</label>
              <input
                type="text"
                placeholder="Enter test title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={inputStyle}
              />
            </div>
            <div>
              <label className={labelStyle}>Test Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={inputStyle}
              />
            </div>
            <div>
              <label className={labelStyle}>Timer</label>
              <input
                type="number"
                placeholder="Minutes"
                min="1"
                value={timerMinutes}
                onChange={(e) => setTimerMinutes(e.target.value)}
                className={inputStyle}
              />
            </div>
            <div>
              <label className={labelStyle}>Section</label>
              <input
                type="text"
                placeholder="Optional section"
                value={section}
                onChange={(e) => setSection(e.target.value)}
                className={inputStyle}
              />
            </div>
            <div>
              <label className={labelStyle}>Access Control</label>
              <select
                value={isFree}
                onChange={(e) => setIsFree(e.target.value === "true")}
                className={inputStyle}
              >
                <option value="true">Free Test</option>
                <option value="false">Premium Test</option>
              </select>
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="mb-5 flex flex-col gap-3 border-b border-slate-200 pb-4 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-bold text-slate-950 dark:text-white">
                {editingIndex !== null
                  ? `Editing Question ${editingIndex + 1}`
                  : "Add Question"}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Compose the question, attach visuals, and mark the correct answer.
              </p>
            </div>
            {editingIndex !== null && (
              <button
                type="button"
                onClick={() => {
                  setEditingIndex(null);
                  setCurrentQuestion({
                    question: "",
                    options: ["", "", "", ""],
                    correctAnswer: "",
                    explanation: "",
                    images: [],
                    tags: "",
                  });
                }}
                className={secondaryButton}
              >
                Cancel Editing
              </button>
            )}
          </div>

          <div className="space-y-5">
            <div>
              <label className={labelStyle}>Question Content</label>
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                <ReactQuill
                  className="min-h-40 bg-white dark:bg-slate-900"
                  theme="snow"
                  value={currentQuestion.question}
                  onChange={(value) =>
                    setCurrentQuestion({
                      ...currentQuestion,
                      question: value,
                    })
                  }
                  modules={quillModules}
                  formats={quillFormats}
                />
              </div>
            </div>

            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-950">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100">
                    Question Images, Tables, Maps, or Graphs
                  </label>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Upload one or more image files for this question.
                  </p>
                </div>
                <label className="inline-flex cursor-pointer items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700">
                  Choose Images
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="sr-only"
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);

                      setUploadingImage(true);

                      const uploadedUrls = [];

                      for (const file of files) {
                        const url = await uploadQuestionImage(file);

                        if (url) uploadedUrls.push(url);
                      }

                      setUploadingImage(false);
                      setCurrentQuestion((prev) => ({
                        ...prev,
                        images: [...prev.images, ...uploadedUrls],
                      }));
                    }}
                  />
                </label>
              </div>

              {uploadingImage && (
                <p className="mt-3 text-sm font-medium text-indigo-600 dark:text-indigo-400">
                  Uploading image...
                </p>
              )}

              {currentQuestion.images?.length > 0 && (
                <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                  {currentQuestion.images.map((url, idx) => (
                    <img
                      key={idx}
                      src={url}
                      alt=""
                      className="h-28 w-full rounded-lg border border-slate-200 object-cover shadow-sm dark:border-slate-700"
                    />
                  ))}
                </div>
              )}

              {currentQuestion.images.length > 0 && (
                <button
                  type="button"
                  onClick={() =>
                    setCurrentQuestion((prev) => ({
                      ...prev,
                      images: [],
                    }))
                  }
                  className="mt-3 text-sm font-semibold text-red-600 hover:text-red-700 dark:text-red-400"
                >
                  Remove All Images
                </button>
              )}
            </div>

            <div>
              <h4 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Options
              </h4>
              <div className="grid gap-3 md:grid-cols-2">
                {currentQuestion.options.map((opt, i) => (
                  <div key={i}>
                    <label className={labelStyle}>Option {optionLabels[i]}</label>
                    <input
                      type="text"
                      placeholder={`Enter option ${optionLabels[i]}`}
                      value={opt}
                      onChange={(e) => {
                        const updated = [...currentQuestion.options];
                        updated[i] = e.target.value;

                        setCurrentQuestion({
                          ...currentQuestion,
                          options: updated,
                          correctAnswer:
                            currentQuestion.correctAnswer ===
                            currentQuestion.options[i]
                              ? e.target.value
                              : currentQuestion.correctAnswer,
                        });
                      }}
                      className={inputStyle}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className={labelStyle}>Correct Answer</label>
                <select
                  value={currentQuestion.correctAnswer}
                  onChange={(e) =>
                    setCurrentQuestion({
                      ...currentQuestion,
                      correctAnswer: e.target.value,
                    })
                  }
                  className={inputStyle}
                >
                  <option value="">Select correct option</option>
                  {currentQuestion.options.map((opt, idx) => (
                    <option key={idx} value={opt}>
                      {optionLabels[idx]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelStyle}>Tags</label>
                <input
                  type="text"
                  placeholder="Polity, Parliament, FRs"
                  value={currentQuestion.tags}
                  onChange={(e) =>
                    setCurrentQuestion({
                      ...currentQuestion,
                      tags: e.target.value,
                    })
                  }
                  className={inputStyle}
                />
              </div>
            </div>

            <div>
              <label className={labelStyle}>Explanation</label>
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                <ReactQuill
                  className="min-h-32 bg-white dark:bg-slate-900"
                  theme="snow"
                  value={currentQuestion.explanation}
                  onChange={(value) =>
                    setCurrentQuestion({
                      ...currentQuestion,
                      explanation: value,
                    })
                  }
                  modules={quillModules}
                  formats={quillFormats}
                />
              </div>
            </div>

            <button
              onClick={handleAddQuestion}
              className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
            >
              {editingIndex !== null ? "Update Question" : "Add Question"}
            </button>
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-bold text-slate-950 dark:text-white">
                Questions Preview
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Review added questions before saving or publishing.
              </p>
            </div>
            <button
              onClick={() => setShowPreview(!showPreview)}
              className={secondaryButton}
            >
              {showPreview ? "Hide" : "Show"} Preview ({questions.length})
            </button>
          </div>

          {showPreview && (
            <div className="space-y-3">
              {questions.length === 0 && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400">
                  No questions added yet.
                </div>
              )}

              {questions.map((q, idx) => (
                <details
                  key={idx}
                  className="group rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 font-semibold text-slate-950 dark:text-white">
                    <span>Question {idx + 1}</span>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      {q.images?.length || 0} images
                    </span>
                  </summary>
                  <div className="mt-4 space-y-3 text-slate-900 dark:text-slate-100">
                    <div
                      className="prose max-w-none dark:prose-invert"
                      dangerouslySetInnerHTML={{
                        __html: q.question,
                      }}
                    />

                    {q.images?.length > 0 && (
                      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                        {q.images.map((img, i) => (
                          <img
                            key={i}
                            src={img}
                            alt={`question-${i}`}
                            className="h-24 w-full rounded-lg border border-slate-200 object-cover dark:border-slate-700"
                          />
                        ))}
                      </div>
                    )}

                    {q.options?.length > 0 && (
                      <div className="grid gap-2 md:grid-cols-2">
                        {q.options.map((opt, i) => (
                          <div
                            key={i}
                            className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-800 dark:bg-slate-900"
                          >
                            <span className="font-semibold">{optionLabels[i]}</span>{" "}
                            {opt}
                          </div>
                        ))}
                      </div>
                    )}

                    <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                      Correct Answer:{" "}
                      {optionLabels[q.options.indexOf(q.correctAnswer)]} -{" "}
                      {q.correctAnswer}
                    </p>

                    {q.explanation && (
                      <p className="text-sm text-slate-700 dark:text-slate-300">
                        <b>Explanation:</b> {q.explanation}
                      </p>
                    )}

                    {q.tags?.length > 0 && (
                      <p className="text-sm text-indigo-600 dark:text-indigo-400">
                        <b>Tags:</b> {q.tags.join(", ")}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-3 pt-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();

                          setCurrentQuestion({
                            question: q.question,
                            options: [...q.options],
                            correctAnswer: q.correctAnswer,
                            explanation: q.explanation || "",
                            images: q.images || [],
                            tags: Array.isArray(q.tags) ? q.tags.join(", ") : "",
                          });

                          setEditingIndex(idx);
                          window.scrollTo({
                            top: 0,
                            behavior: "smooth",
                          });
                        }}
                        className={secondaryButton}
                      >
                        Edit Question
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();

                          setQuestions((prev) => prev.filter((_, i) => i !== idx));

                          if (editingIndex === idx) {
                            setEditingIndex(null);
                            setCurrentQuestion({
                              question: "",
                              options: ["", "", "", ""],
                              correctAnswer: "",
                              explanation: "",
                              images: [],
                              tags: "",
                            });
                          }
                        }}
                        className="inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100 dark:border-red-900 dark:bg-red-950 dark:text-red-300"
                      >
                        Delete Question
                      </button>
                    </div>
                  </div>
                </details>
              ))}
            </div>
          )}
        </section>

        <div className="sticky bottom-0 z-10 -mx-4 border-t border-slate-200 bg-white/95 px-4 py-4 shadow-lg backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:mx-0 sm:rounded-xl sm:border">
          <div className="mx-auto flex max-w-6xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {questions.length} question{questions.length === 1 ? "" : "s"} ready.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                onClick={() => handleSubmit("draft")}
                disabled={questions.length === 0}
                className="rounded-lg bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-amber-600 dark:hover:bg-amber-700"
              >
                Save as Draft
              </button>
              <button
                onClick={() => handleSubmit("published")}
                disabled={questions.length === 0}
                className="rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-emerald-700 dark:hover:bg-emerald-800"
              >
                Update Test
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPrelimsTest;
